import { type FC, useState } from "react";
import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { ProfileIcon } from "@/components/users/ProfileIcon.tsx";
import { IconShare } from "@/icons/IconShare.tsx";
import { IconPanelRight } from "@/icons/IconPanelRight.tsx";
import { type Chat, useChatStore } from "@/stores/useChatStore.ts";
import type { FlowGeneratorSession } from "@/types/flow.ts";
import type { AssignedSession } from "@/stores/useAssignedChatStore.ts";
import { IconPlus } from "@/icons/IconPlus.tsx";
import { useClaimSession } from "@/helpers/useClaimSession.ts";
import { ModalSimple } from "@/components/modal/ModalSimple.tsx";
import { ButtonIcon } from "@/components/buttons/ButtonIcon.tsx";
import { IconTick } from "@/icons/IconTick.tsx";
import { IconX } from "@/icons/IconX.tsx";
import { Row } from "@/components/common/row.ts";
import { TEACHER_ENDPOINT } from "@/constants/settings.ts";
import { clipboardShareSession, clipboardWriteText } from "@/helpers/clipboard.ts";
import { IsAiTutorId } from "@/helpers/aiTutor.ts";
import { postFlowAiAutoSend } from "@/api/admin.ts";

interface ChatHeaderProps {
  showContext: boolean;
  activeSession?: AssignedSession;
  toggleShowContext: () => void;
  chat: Chat;
  quizShortCode?: string;
  isAiSession?: boolean;
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  height: 70px;

  padding: 15px ${t.chatPaddingSides};
  border-bottom: ${t.brandBorderSegment};
  gap: 5px;

  @container style(--small-height: 1) {
    padding: 5px ${t.chatPaddingSides};
    height: 50px;
  }
`;

const LeftContent = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 1;
  flex-grow: 1;
`;

const RightContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  gap: 10px;
`;

const SandwichedText = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Name = styled.p`
  font-size: var(--16px);
  font-weight: 600;
  font-family: ${t.fontSecondary};

  transition: all 150ms ease;
  cursor: pointer;

  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
    color: ${t.brandPastelBlue};
  }
`;

const Description = styled.a`
  font-size: var(--13px);
  font-weight: 400;
  font-family: ${t.fontSecondary};
  color: ${t.fontPrimary};

  text-overflow: ellipsis;
  white-space: nowrap;

  transition: all 150ms ease;

  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: ${t.brandPastelBlue};
  }

  &[data-invalid="true"] {
    color: ${t.fontSecondary};
    text-decoration: none;
  }
`;

const SpanButton = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  line-height: 1.75rem;
  border: 1px solid #848395;
  padding: 2px 10px;
  border-radius: ${t.brandBorderRadius};
  transition: all 0.5s
    linear(
      0,
      1.114 8.5%,
      1.37 12.9%,
      1.315 16.2%,
      0.941 24%,
      0.869 27.8%,
      0.882 30.7%,
      1.012 38.3%,
      1.046 42.7%,
      0.984,
      1.006 72.2%,
      1
    );

  &[data-loading="true"] {
    scale: 95%;
    cursor: wait;
    color: ${t.brandPastelBlue};
    border-color: ${t.brandPastelBlue};
  }

  &:hover {
    scale: 105%;
  }

  &:active {
    scale: 90%;
  }

  &[data-show="true"] {
    color: ${t.brandPastelBlue};
    border-color: ${t.brandPastelBlue};
  }
`;

const OverrideText = styled.p`
  padding: 1rem 0;
`;

const Separator = styled.div`
  width: 1px;
  height: 20px;
  background-color: #cbd5e1;
  margin: 0 1rem 0 0.5rem;
`;

export const ChatHeader: FC<ChatHeaderProps> = ({
  chat,
  activeSession,
  showContext,
  toggleShowContext,
  quizShortCode,
  isAiSession,
}) => {
  const flowSession = chat.flowGeneratorSession as FlowGeneratorSession;
  const { SetChatActive } = useChatStore();
  const [showOverride, setShowOverride] = useState(false);

  const { attemptClaim: attemptClaimSession, isPending: isClaimPending } = useClaimSession(
    chat.flowGeneratorSessionId,
    () => setShowOverride(true),
  );

  const canClaimSession = !activeSession || activeSession.completed;
  const firstName = flowSession.firstName ?? flowSession.student?.firstName ?? "Unknown";
  const lastName = flowSession.lastName ?? flowSession.student?.lastName ?? "Unknown";
  const quiz = flowSession.quizName ?? flowSession.metaData?.quizName ?? "Unknown";

  return (
    <>
      <Header>
        <LeftContent>
          <ProfileIcon
            userId={flowSession.userId}
            size={"40px"}
            ai={isAiSession}
            aiSize={20}
            data-tooltip={isAiSession ? "Eedi AI Tutor is handling this session" : undefined}
            data-direction={"right"}
          />
          <SandwichedText>
            <Name
              className={"textWrapMinimal"}
              onClick={() => clipboardWriteText(firstName + " " + lastName)}
            >
              {firstName} {lastName}
            </Name>
            <Description
              className={"textWrapMinimal"}
              href={quizShortCode ? TEACHER_ENDPOINT + "quizzes/" + quizShortCode : undefined}
              data-invalid={!quizShortCode}
              data-tooltip={
                !quizShortCode
                  ? "Quiz not available, Retrieval quizzes are unique to this student"
                  : undefined
              }
              data-direction={"bottom"}
              target="_blank"
            >
              {quiz}
            </Description>
          </SandwichedText>
        </LeftContent>
        <RightContent>
          {canClaimSession && (
            <>
              <SpanButton
                onClick={() => {
                  if (IsAiTutorId(chat.flowGeneratorSession?.tutorId))
                    postFlowAiAutoSend(chat.flowGeneratorSessionId, false).then(() => {
                      attemptClaimSession(true);
                    });
                  else attemptClaimSession(false);
                }}
                data-loading={isClaimPending}
              >
                <IconPlus
                  width={"14"}
                  height={"14"}
                  color={isClaimPending ? t.brandPastelBlue : "#475569"}
                />
                {isClaimPending ? "Claiming..." : "Claim Session"}
              </SpanButton>
              <Separator />
              <SpanButton
                onClick={() => {
                  SetChatActive(null);
                }}
              >
                <IconX width={"20"} height={"20"} color={"#475569"} />
                Close
              </SpanButton>
            </>
          )}
          <SpanButton
            onClick={() => {
              clipboardShareSession(flowSession?.flowGeneratorSessionId);
            }}
          >
            <IconShare />
            Share Session
          </SpanButton>
          <SpanButton onClick={toggleShowContext} data-show={showContext}>
            <IconPanelRight
              width={"22"}
              height={"24"}
              color={showContext ? t.brandPastelBlue : undefined}
            />
            {showContext ? "Hide" : "Show"} Context
          </SpanButton>
        </RightContent>
      </Header>
      {showOverride && (
        <ModalSimple
          title={"Override claim this session?"}
          onClick={() => {
            setShowOverride(false);
          }}
        >
          <OverrideText>
            {isAiSession ? (
              <>
                This session is being run by Eedi's AI Tutor. <br />
                Claiming will take it over and switch it to human moderation — you will review every
                response before it reaches the student.
              </>
            ) : (
              <>
                This session is currently claimed by another tutor! <br />
                Would you like to take over this session?
              </>
            )}
          </OverrideText>
          <Row>
            <ButtonIcon
              icon={<IconX color={t.brandNeutralWhite} />}
              text={"Go back"}
              onClick={() => {
                setShowOverride(false);
              }}
              backgroundColor={t.brandAccentRed}
              flare
            />
            <ButtonIcon
              icon={<IconTick width={18} height={18} />}
              text={"Claim session"}
              onClick={() => {
                setShowOverride(false);
                attemptClaimSession(true);
              }}
              backgroundColor={t.brandAccentBlue}
              flare
            />
          </Row>
        </ModalSimple>
      )}
    </>
  );
};
